import {
  Box,
  Stack,
  TextareaAutosize,
  Typography,
  useTheme,
} from "@mui/material";
import { forwardRef } from "react";

export interface DescriptionEditorProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  minHeight?: string;
  maxHeight?: string;
  disabled?: boolean;
}

/**
 * Plain-text description editor. Visually mimics `CustomQuillEditor` (same
 * gray background, rounded corners, error state, placeholder) but without
 * rich-text formatting. Use when you need a simple multiline description
 * field and don't want the weight of Quill.
 */
export const DescriptionEditor = forwardRef<
  HTMLTextAreaElement,
  DescriptionEditorProps
>(
  (
    {
      name,
      value = "",
      onChange,
      onBlur,
      placeholder,
      error = false,
      errorMessage,
      minHeight = "100px",
      maxHeight,
      disabled = false,
    },
    ref,
  ) => {
    const theme = useTheme();

    return (
      <Stack sx={{ width: "100%" }}>
        <Box
          sx={{
            "& .description-editor-textarea": {
              width: "100%",
              boxSizing: "border-box",
              outline: "none",
              resize: "vertical",
              padding: "16px",
              borderRadius: "8px",
              backgroundColor: theme.palette.neutral["005"],
              border: `1px solid ${
                error ? theme.palette.error.main : theme.palette.neutral["015"]
              }`,
              color: theme.palette.lightBg.high,
              ...theme.typography["body-medium"],
              minHeight,
              maxHeight,
              overflowY: maxHeight ? "auto" : undefined,
              transition: "border-color 0.15s ease",
              "&::placeholder": {
                color: theme.palette.lightBg.low,
              },
              "&:hover:not(:disabled)": {
                borderColor: error
                  ? theme.palette.error.main
                  : theme.palette.lightBg.medium,
              },
              "&:focus": {
                borderColor: error
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
              },
              "&:disabled": {
                cursor: "not-allowed",
                opacity: 0.6,
              },
            },
          }}
        >
          <TextareaAutosize
            ref={ref}
            className="description-editor-textarea"
            name={name}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
          />
        </Box>
        {errorMessage && (
          <Box sx={{ pl: "14px", mt: "4px" }}>
            <Typography variant="body-small" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  },
);

DescriptionEditor.displayName = "DescriptionEditor";
